import React, { useState, useEffect } from "react";
import 'antd/dist/reset.css';
import {
  Button,
  Table,
  Skeleton,
  Modal,
  Select,
  Form,
  Popconfirm,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { MESSAGES } from "./Notification";
import { fetchEmployeeData, showErrorNotification, showNotification } from "../../../utils/documentConfig";

import { addUserTraining, deleteUserTraining, getAvailableTrainings, getUserTrainings } from "../../../services/Service";
import { LABELS } from "./labels";



const { Option } = Select;

const UserAttendedTrainings = () => {
  const [visible, setVisible] = useState(false);
  const { id } = useParams();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addLoading, setAddLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedTrainingName, setSelectedTrainingName] = useState(null);
  const [availableTrainings, setAvailableTrainings] = useState([]);
  const email = localStorage.getItem("userEmail");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const roleUser = JSON.parse(localStorage.getItem("userRoles"));
    if (roleUser && roleUser.includes("ADMINUSER")) {
      setIsAdmin(true);
    }

    if (!id) {
      fetchUserData();
    } else {
      setUserId(id);
    }
  }, [id]);

  useEffect(() => {
    if (userId) {
      fetchTrainings();
      fetchTrainingsList();
    }
  }, [userId]);

  const fetchUserData = async () => {
    try {
      const fetchedUserId = await fetchEmployeeData(email);
      setUserId(fetchedUserId);
    } catch (error) {
      showErrorNotification(MESSAGES.FETCH_USER_ERROR);
    }
  };

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const response = await getUserTrainings(userId);
      setTrainings(response.data.trainingName || []);
    } catch (error) {
      showErrorNotification(MESSAGES.FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingsList = async () => {
    try {
      const response = await getAvailableTrainings();
      if (Array.isArray(response.data)) {
        setAvailableTrainings(response.data);
      }
    } catch (error) {
      showErrorNotification(MESSAGES.FETCH_ERROR);
    }
  };

  const onCreate = async () => {
    if (!selectedTrainingName) {
      showErrorNotification(MESSAGES.VALIDATION_ERROR);
      return;
    }

    const selectedTraining = availableTrainings.find(
      (t) => t.id === selectedTrainingName
    );

    if (!selectedTraining) {
      showErrorNotification("Selected training not found.");
      return;
    }

    const alreadyEnrolled = trainings.some(
      (t) => t.trainingName === selectedTraining.trainingName
    );

    if (alreadyEnrolled) {
      showErrorNotification(MESSAGES.DUPLICATE_TRAINING(selectedTraining.trainingName));
      return;
    }

    try {
      setAddLoading(true);

      const trainingPayload = {
        uid: userId,
        trainingId: selectedTraining.id,
        trainingName: selectedTraining.trainingName,
      };

      await addUserTraining(userId, trainingPayload);

      showNotification(MESSAGES.ADD_SUCCESS);

      setVisible(false);
      setSelectedTrainingName(null);
      await fetchTrainings();
    } catch (error) {
      showErrorNotification(MESSAGES.ADD_ERROR);
    } finally {
      setAddLoading(false);
    }
  };

  const onDelete = async (record) => {
    try {
      await deleteUserTraining(userId, record.id);
      setTrainings(trainings.filter((t) => t.id !== record.id));
      showNotification(MESSAGES.DELETE_SUCCESS);
    } catch (error) {
      showErrorNotification(MESSAGES.DELETE_ERROR);
    }
  };

  const columns = [
    {
      title: LABELS.TRAINING_NAME_LABEL,
      dataIndex: "trainingName",
      key: "trainingName",
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this training?"
          onConfirm={() => onDelete(record)}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="flex-1 p-6 background-light min-h-screen pt-30">
      <h1 className="text-2xl text-primary font-bold ml-0">{LABELS.PAGE_TITLE}</h1>

     
        {!isAdmin && (
          <div className="flex justify-end mb-4 mr-4">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setVisible(true)}
              className="text-white border p-4 bg-[var(--color-primary)]"
            >
              {LABELS.ADD_BUTTON}
            </Button>
          </div>
        )}
      

      <Modal
        title={LABELS.MODAL_TITLE}
        open={visible}
        onCancel={() => setVisible(false)}
        confirmLoading={addLoading}
        onOk={onCreate}
        okText="Add"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <Form.Item label={LABELS.TRAINING_NAME_LABEL} required>
            <Select
              value={selectedTrainingName}
              onChange={setSelectedTrainingName}
              placeholder="Select a training"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableTrainings.map((training) => {
                const enrolled = trainings.some(
                  (userTraining) => userTraining.trainingName === training.trainingName
                );
                return (
                  <Option key={training.id} value={training.id} disabled={enrolled}>
                    {training.trainingName}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={trainings}
        loading={loading}
        rowKey="id"
        pagination={false}
        scroll={{ y: 240 }}
        locale={{
          emptyText: loading ? <Skeleton active /> : "No Trainings Found",
        }}
        className="custom-header-userattended"
        bordered
      />
    </div>
  );
};

export default UserAttendedTrainings;
